// src/components/ReportScheduleManager.tsx
// Admin interface for managing Power BI report schedules and email recipients
// Uses SharePoint Lists for storage when configured, falls back to local state

import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  Mail,
  Plus,
  Pencil,
  Trash2,
  X,
  CheckCircle,
  Save,
  AlertCircle,
  Truck,
  RefreshCw,
  Settings,
  History,
  ArrowLeft,
  Cloud,
  CloudOff
} from 'lucide-react';
import {
  getRecipients,
  createRecipient,
  updateRecipient,
  deleteRecipient as deleteRecipientSP,
  getDrivers,
  createDriver,
  updateDriver,
  getRunHistory,
  type Recipient,
  type Driver,
  type RunHistoryItem
} from '../services/sharePointService';
import CalendarScheduleTab from './CalendarScheduleTab';

// ══════════════════════════════════════════════════════════════════════════════
// Types
// ══════════════════════════════════════════════════════════════════════════════

interface ReportScheduleManagerProps {
  onBack?: () => void;
}

// ══════════════════════════════════════════════════════════════════════════════
// Configuration
// ══════════════════════════════════════════════════════════════════════════════


// ══════════════════════════════════════════════════════════════════════════════
// Mock Data (fallback when SharePoint not configured)
// ══════════════════════════════════════════════════════════════════════════════


const mockRecipients: Recipient[] = [
  { id: '1', name: 'Operations Team', email: 'ops@delldata.com', groups: ['LoadTeam', 'Management'], isActive: true },
  { id: '2', name: 'Warehouse Manager', email: 'warehouse@delldata.com', groups: ['LoadTeam'], isActive: true },
  { id: '3', name: 'Logistics Lead', email: 'logistics@delldata.com', groups: ['SalesTeam'], isActive: false },
];

const mockDrivers: Driver[] = [
  { id: '1', driverCode: '001', driverName: 'John Smith',    email: 'john@delldata.com',                                        isActive: true },
  { id: '2', driverCode: '002', driverName: 'Mike Johnson',  email: 'mike@delldata.com, dispatch@delldata.com',              isActive: true },
  { id: '3', driverCode: '003', driverName: 'David Williams',email: 'david@delldata.com',                                       isActive: true },
];

const mockHistory: RunHistoryItem[] = [
  { id: '1', title: 'LoadSheet-S1-2026-03-08', runDate: '2026-03-08', reportType: 'LoadSheet', sendNumber: 1, status: 'Success',  recipientCount: 8,  runAt: '2026-03-08T21:05:00Z' },
  { id: '2', title: 'LoadSheet-S2-2026-03-08', runDate: '2026-03-08', reportType: 'LoadSheet', sendNumber: 2, status: 'Success',  recipientCount: 12, runAt: '2026-03-08T23:30:00Z' },
  { id: '3', title: 'LoadSheet-S1-2026-03-07', runDate: '2026-03-07', reportType: 'LoadSheet', sendNumber: 1, status: 'Success',  recipientCount: 8,  runAt: '2026-03-07T21:05:00Z' },
  { id: '4', title: 'LoadSheet-S2-2026-03-06', runDate: '2026-03-06', reportType: 'LoadSheet', sendNumber: 2, status: 'Skipped', recipientCount: 0,  skipReason: 'Shabbat', runAt: '2026-03-06T23:30:00Z' },
];

// ══════════════════════════════════════════════════════════════════════════════
// Main Component
// ══════════════════════════════════════════════════════════════════════════════

const ReportScheduleManager: React.FC<ReportScheduleManagerProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<'schedules' | 'recipients' | 'drivers' | 'history'>('schedules');
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [newRecipient, setNewRecipient] = useState({ name: '', email: '', groups: [] as string[] });
  const [editingRecipient, setEditingRecipient] = useState<Recipient | null>(null);
  const [recipientErrors, setRecipientErrors] = useState({ name: '', email: '', groups: '' });
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [addingDriver, setAddingDriver] = useState(false);
  const [newDriver, setNewDriver] = useState({ driverCode: '', driverName: '', email: '' });
  const [history, setHistory] = useState<RunHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // ════════════════════════════════════════════════════════════════════════════
  // Data Loading
  // ════════════════════════════════════════════════════════════════════════════

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [recipientsData, driversData, historyData] = await Promise.all([
        getRecipients(),
        getDrivers(),
        getRunHistory(),
      ]);
      setRecipients(recipientsData);
      setDrivers(driversData);
      setHistory(historyData);
      setIsConnected(true);
    } catch (err: unknown) {
      console.error('SharePoint load error:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      
      // Check if it's a configuration error vs data error
      if (errorMessage.includes('not configured') || errorMessage.includes('Missing')) {
        setError(`Configuration error: ${errorMessage}`);
      } else {
        setError(`SharePoint error: ${errorMessage}`);
      }
      
      // Fall back to mock data
      setRecipients(mockRecipients);
      setDrivers(mockDrivers);
      setHistory(mockHistory);
      setIsConnected(false);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ════════════════════════════════════════════════════════════════════════════
  // Schedule Management
  // ════════════════════════════════════════════════════════════════════════════

  // ════════════════════════════════════════════════════════════════════════════
  // Recipient Management
  // ════════════════════════════════════════════════════════════════════════════

  const addRecipient = async (recipient: Omit<Recipient, 'id'>) => {
    if (isConnected) {
      try {
        const newRecipient = await createRecipient(recipient);
        setRecipients(prev => [...prev, newRecipient]);
      } catch (err) {
        setError('Failed to add recipient');
      }
    } else {
      setRecipients(prev => [...prev, { ...recipient, id: Date.now().toString() }]);
    }
  };

  const toggleRecipientActive = async (id: string) => {
    const recipient = recipients.find(r => r.id === id);
    if (!recipient) return;

    const newActive = !recipient.isActive;
    setRecipients(prev => prev.map(r => (r.id === id ? { ...r, isActive: newActive } : r)));

    if (isConnected) {
      try {
        await updateRecipient(id, { isActive: newActive });
      } catch (err) {
        setRecipients(prev => prev.map(r => (r.id === id ? { ...r, isActive: !newActive } : r)));
        setError('Failed to update recipient');
      }
    }
  };

  const handleDeleteRecipient = async (id: string) => {
    const original = recipients;
    setRecipients(prev => prev.filter(r => r.id !== id));

    if (isConnected) {
      try {
        await deleteRecipientSP(id);
      } catch (err) {
        setRecipients(original);
        setError('Failed to delete recipient');
      }
    }
  };


  const saveEditRecipient = async () => {
    if (!editingRecipient) return;
    const original = recipients;
    setRecipients(prev => prev.map(r => r.id === editingRecipient.id ? editingRecipient : r));
    setEditingRecipient(null);
    if (isConnected) {
      try {
        await updateRecipient(editingRecipient.id, {
          name: editingRecipient.name,
          email: editingRecipient.email,
          groups: editingRecipient.groups,
        });
      } catch (err) {
        setRecipients(original);
        setError('Failed to update recipient');
      }
    }
  };

  // ════════════════════════════════════════════════════════════════════════════
  // Helper Functions
  // ════════════════════════════════════════════════════════════════════════════

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };
  // ════════════════════════════════════════════════════════════════════════════
  // Tab Content: Recipients
  // ════════════════════════════════════════════════════════════════════════════

  const renderRecipientsTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Email Recipients</h2>
        <span className="text-sm text-slate-400">
          {recipients.filter(r => r.isActive).length} active of {recipients.length}
        </span>
      </div>

      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="divide-y divide-slate-700/50">
          {recipients.map(recipient => (
            <div key={recipient.id} className="p-4 hover:bg-slate-700/30">
              {editingRecipient?.id === recipient.id ? (
                /* ── Edit mode ── */
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={editingRecipient.name}
                      onChange={e => setEditingRecipient({ ...editingRecipient, name: e.target.value })}
                      placeholder="Name"
                      className="flex-1 px-3 py-2 bg-slate-700 border border-slate-500 rounded-lg text-sm text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <input
                      type="email"
                      value={editingRecipient.email}
                      onChange={e => setEditingRecipient({ ...editingRecipient, email: e.target.value })}
                      placeholder="Email"
                      className="flex-1 px-3 py-2 bg-slate-700 border border-slate-500 rounded-lg text-sm text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-3 items-center">
                      <span className="text-xs text-slate-400">Groups</span>
                      {(['LoadTeam', 'SalesTeam', 'Management', 'OperationsTeam'] as const).map(g => (
                        <label key={g} className="flex items-center gap-1.5 text-sm text-slate-400 cursor-pointer hover:text-white">
                          <input
                            type="checkbox"
                            checked={editingRecipient.groups.includes(g)}
                            onChange={e => setEditingRecipient({
                              ...editingRecipient,
                              groups: e.target.checked
                                ? [...editingRecipient.groups, g]
                                : editingRecipient.groups.filter(x => x !== g)
                            })}
                            className="rounded"
                          />
                          {g}
                        </label>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingRecipient(null)}
                        className="px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveEditRecipient}
                        className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* ── View mode ── */
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                      recipient.isActive ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-700 text-slate-500'
                    }`}>
                      {recipient.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className={`font-medium ${recipient.isActive ? 'text-white' : 'text-slate-500'}`}>
                        {recipient.name}
                      </p>
                      <p className="text-sm text-slate-400">{recipient.email}</p>
                      <div className="flex gap-1 mt-1">
                        {recipient.groups.map(g => (
                          <span key={g} className="px-1.5 py-0.5 text-xs rounded bg-slate-700 text-slate-400">
                            {g}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleRecipientActive(recipient.id)}
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                        recipient.isActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-500'
                      }`}
                    >
                      {recipient.isActive ? 'Active' : 'Inactive'}
                    </button>
                    <button
                      onClick={() => setEditingRecipient(recipient)}
                      className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded transition-colors"
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      onClick={() => handleDeleteRecipient(recipient.id)}
                      className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {recipients.length === 0 && (
            <div className="p-8 text-center text-slate-500">
              <Mail size={32} className="mx-auto mb-2 opacity-50" />
              <p>No recipients configured</p>
            </div>
          )}
        </div>

        {/* Add recipient form */}
        <div className="p-4 border-t border-slate-700/50 bg-slate-800/80 space-y-3">
          <div className="flex gap-3">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Name"
                value={newRecipient.name}
                onChange={e => { setNewRecipient(r => ({ ...r, name: e.target.value })); setRecipientErrors(err => ({ ...err, name: '' })); }}
                className={`w-full px-3 py-2 bg-slate-700 border rounded-lg text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${recipientErrors.name ? 'border-red-500' : 'border-slate-600'}`}
              />
              {recipientErrors.name && <p className="text-xs text-red-400 mt-1">{recipientErrors.name}</p>}
            </div>
            <div className="flex-1">
              <input
                type="email"
                placeholder="Email address"
                value={newRecipient.email}
                onChange={e => { setNewRecipient(r => ({ ...r, email: e.target.value })); setRecipientErrors(err => ({ ...err, email: '' })); }}
                className={`w-full px-3 py-2 bg-slate-700 border rounded-lg text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${recipientErrors.email ? 'border-red-500' : 'border-slate-600'}`}
              />
              {recipientErrors.email && <p className="text-xs text-red-400 mt-1">{recipientErrors.email}</p>}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex gap-3 items-center">
                <span className="text-xs text-slate-400">Group <span className="text-red-400">*</span></span>
                {(['LoadTeam', 'SalesTeam', 'Management', 'OperationsTeam'] as const).map(g => (
                  <label key={g} className="flex items-center gap-1.5 text-sm text-slate-400 cursor-pointer hover:text-white">
                    <input
                      type="checkbox"
                      checked={newRecipient.groups.includes(g)}
                      onChange={e => {
                        setNewRecipient(r => ({ ...r, groups: e.target.checked ? [...r.groups, g] : r.groups.filter(x => x !== g) }));
                        setRecipientErrors(err => ({ ...err, groups: '' }));
                      }}
                      className="rounded"
                    />
                    {g}
                  </label>
                ))}
              </div>
              {recipientErrors.groups && <p className="text-xs text-red-400">{recipientErrors.groups}</p>}
            </div>
            <button
              onClick={() => {
                const errors = { name: '', email: '', groups: '' };
                if (!newRecipient.name.trim())        errors.name   = 'Name is required';
                if (!newRecipient.email.trim())       errors.email  = 'Email is required';
                if (newRecipient.groups.length === 0) errors.groups = 'Please select at least one group';
                if (errors.name || errors.email || errors.groups) { setRecipientErrors(errors); return; }
                addRecipient({ ...newRecipient, isActive: true });
                setNewRecipient({ name: '', email: '', groups: [] });
                setRecipientErrors({ name: '', email: '', groups: '' });
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
            >
              <Plus size={16} />
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );


  // ════════════════════════════════════════════════════════════════════════════
  // Driver Management
  // ════════════════════════════════════════════════════════════════════════════

  const addDriver = async () => {
    if (!newDriver.driverCode || !newDriver.driverName) return;
    try {
      const created = await createDriver({ ...newDriver, isActive: true });
      setDrivers(prev => [...prev, created]);
      setNewDriver({ driverCode: '', driverName: '', email: '' });
      setAddingDriver(false);
    } catch (err) {
      setError('Failed to add driver');
    }
  };

  const saveDriver = async (driver: Driver) => {
    setDrivers(prev => prev.map(d => (d.id === driver.id ? driver : d)));
    setEditingDriver(null);
    if (isConnected) {
      try {
        await updateDriver(driver.id, { email: driver.email, isActive: driver.isActive });
      } catch (err) {
        setError('Failed to save driver');
        await loadData(); // revert
      }
    }
  };

  const toggleDriverActive = async (id: string) => {
    const driver = drivers.find(d => d.id === id);
    if (!driver) return;
    const newActive = !driver.isActive;
    setDrivers(prev => prev.map(d => (d.id === id ? { ...d, isActive: newActive } : d)));
    if (isConnected) {
      try {
        await updateDriver(id, { isActive: newActive });
      } catch (err) {
        setDrivers(prev => prev.map(d => (d.id === id ? { ...d, isActive: !newActive } : d)));
        setError('Failed to update driver');
      }
    }
  };

  // ════════════════════════════════════════════════════════════════════════════
  // Tab Content: Drivers
  // ════════════════════════════════════════════════════════════════════════════

  const renderDriversTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Drivers</h2>
        <button
          onClick={() => setAddingDriver(true)}
          className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={14} />
          Add Driver
        </button>
      </div>

      {/* Add Driver Form */}
      {addingDriver && (
        <div className="bg-slate-700/50 border border-slate-600 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-medium text-white">New Driver</h3>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Code (e.g. 004)"
              value={newDriver.driverCode}
              onChange={e => setNewDriver(d => ({ ...d, driverCode: e.target.value }))}
              className="px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-sm text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <input
              type="text"
              placeholder="Driver name"
              value={newDriver.driverName}
              onChange={e => setNewDriver(d => ({ ...d, driverName: e.target.value }))}
              className="px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-sm text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <input
              type="text"
              placeholder="Email(s) — separate multiple with commas"
              value={newDriver.email}
              onChange={e => setNewDriver(d => ({ ...d, email: e.target.value }))}
              className="col-span-2 px-3 py-2 bg-slate-600 border border-slate-500 rounded-lg text-sm text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setAddingDriver(false); setNewDriver({ driverCode: '', driverName: '', email: '' }); }}
              className="px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={addDriver}
              disabled={!newDriver.driverCode || !newDriver.driverName}
              className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <CheckCircle size={14} />
              Save Driver
            </button>
          </div>
        </div>
      )}

      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
        {drivers.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Truck size={32} className="mx-auto mb-2 opacity-50" />
            <p>No drivers configured</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-800/80 border-b border-slate-700/50">
              <tr>

                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Driver</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Email(s)</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-400 uppercase tracking-wider">Active</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {drivers.map(driver => {
                const isEditing = editingDriver?.id === driver.id;
                const row = isEditing ? editingDriver! : driver;
                return (
                  <tr key={driver.id} className={`transition-colors ${isEditing ? 'bg-slate-700/40' : 'hover:bg-slate-700/30'}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center text-sm font-bold">
                          {driver.driverCode}
                        </span>
                        <span className="font-medium text-white">{driver.driverName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <input
                          type="email"
                          value={row.email}
                          onChange={e => setEditingDriver({ ...row, email: e.target.value })}
                          className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="primary@email.com"
                        />
                      ) : (
                        <span className="text-sm text-slate-400">{driver.email || '—'}</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-center">
                      {isEditing ? (
                        <button
                          onClick={() => setEditingDriver({ ...row, isActive: !row.isActive })}
                          className={`px-2 py-1 text-xs rounded-full font-medium transition-colors ${
                            row.isActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-500'
                          }`}
                        >
                          {row.isActive ? 'Active' : 'Inactive'}
                        </button>
                      ) : (
                        <button
                          onClick={() => toggleDriverActive(driver.id)}
                          className={`px-2 py-1 text-xs rounded-full font-medium transition-colors ${
                            driver.isActive ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'bg-slate-700 text-slate-500 hover:bg-slate-600'
                          }`}
                          title="Click to toggle active"
                        >
                          {driver.isActive ? 'Active' : 'Inactive'}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isEditing ? (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setEditingDriver(null)}
                            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded transition-colors"
                          >
                            <X size={15} />
                          </button>
                          <button
                            onClick={() => saveDriver(row)}
                            className="p-1.5 bg-blue-600 text-white hover:bg-blue-700 rounded transition-colors"
                          >
                            <Save size={15} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditingDriver({ ...driver })}
                          className="px-3 py-1 text-xs text-slate-400 hover:text-white bg-slate-700/50 hover:bg-slate-600 rounded transition-colors"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );


  // ════════════════════════════════════════════════════════════════════════════
  // Tab Content: History
  // ════════════════════════════════════════════════════════════════════════════

  const renderHistoryTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-white">Run History</h2>
        <button
          onClick={loadData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
        {history.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <History size={32} className="mx-auto mb-2 opacity-50" />
            <p>No run history yet</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-700/50">
            {history.map(item => (
              <div key={item.id} className="p-4 flex items-center justify-between hover:bg-slate-700/30">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    item.status === 'Success' ? 'bg-emerald-500/20 text-emerald-400'
                    : item.status === 'Failed'  ? 'bg-red-500/20 text-red-400'
                    : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {item.status === 'Success' ? <CheckCircle size={20} />
                    : item.status === 'Failed'  ? <AlertCircle size={20} />
                    : <X size={20} />}
                  </div>
                  <div>
                    <p className="font-medium text-white">{item.title}</p>
                    <p className="text-sm text-slate-400">
                      Send {item.sendNumber} · {item.reportType} · {item.recipientCount} recipients
                      {item.skipReason && <span className="text-amber-400"> · {item.skipReason}</span>}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-300">{formatDateTime(item.runAt)}</p>
                  <p className={`text-xs font-medium ${
                    item.status === 'Success' ? 'text-emerald-400'
                    : item.status === 'Failed'  ? 'text-red-400'
                    : 'text-amber-400'
                  }`}>
                    {item.status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );


  // ════════════════════════════════════════════════════════════════════════════
  // Schedule Edit Modal
  // ════════════════════════════════════════════════════════════════════════════

  // ════════════════════════════════════════════════════════════════════════════
  // Main Render
  // ════════════════════════════════════════════════════════════════════════════

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Decorative background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-4">
              {onBack && (
                <button
                  onClick={onBack}
                  className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
                >
                  <ArrowLeft size={20} />
                </button>
              )}
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <Settings className="text-blue-400" size={28} />
                Report Schedule Manager
              </h1>
            </div>

            {/* Connection Status */}
            <div className="flex items-center gap-3">
              {isConnected ? (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <Cloud size={16} className="text-emerald-400" />
                  <span className="text-sm text-emerald-400">Connected to SharePoint</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <CloudOff size={16} className="text-amber-400" />
                  <span className="text-sm text-amber-400">Local Mode</span>
                </div>
              )}
              <button
                onClick={loadData}
                disabled={loading}
                className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all disabled:opacity-50"
                title="Refresh data"
              >
                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
          <p className="text-slate-400 ml-12">
            Configure automated report delivery schedules and email recipients
          </p>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 flex items-center gap-3">
            <AlertCircle size={20} />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto p-1 hover:bg-red-500/20 rounded">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-1 p-1 bg-slate-800/50 rounded-xl mb-6 w-fit border border-slate-700/50">
          {[
            { id: 'schedules', label: 'Schedules', icon: Calendar },
            { id: 'recipients', label: 'Recipients', icon: Mail },
            { id: 'drivers', label: 'Drivers', icon: Truck },
            { id: 'history', label: 'History', icon: History },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="bg-slate-800/30 rounded-2xl p-6 border border-slate-700/50">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={32} className="animate-spin text-blue-400" />
            </div>
          ) : (
            <>
              {activeTab === 'schedules' && <CalendarScheduleTab />}
              {activeTab === 'recipients' && renderRecipientsTab()}
              {activeTab === 'drivers' && renderDriversTab()}
              {activeTab === 'history' && renderHistoryTab()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportScheduleManager;